import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import { useMapAnalytics } from '../../hooks/useMapAnalytics'
import { trackSpotModeActivation } from '../../utils/analytics'
import { createLogger } from '../../utils/logger'

const logger = createLogger('MyLocationControl')

/**
 * MyLocationControl - Adds "My Location" and Zoom controls to the map
 * 
 * This component creates custom Leaflet controls that match the default
 * Leaflet styling. The location button is positioned above zoom controls.
 * 
 * Features:
 * - Spot Mode button (top, optional)
 * - My Location button
 * - Zoom In button
 * - Zoom Out button
 * - All styled consistently with Leaflet's default controls
 * - Geolocation with high accuracy
 * - Loading state while fetching location
 * - Error handling with user feedback
 * - Analytics tracking
 * 
 * @param {Function} onLocationClick - Callback to handle location button clicks (optional)
 *                                      If provided, uses this instead of built-in geolocation
 * @param {Function} onSpotClick - Callback to handle Spot Mode activation (optional)
 *                                  If provided, shows Spot button at top
 */
export function MyLocationControl({ onLocationClick, onSpotClick }) {
  const map = useMap()
  const { trackMapPan, trackMapZoom } = useMapAnalytics()
  const controlRef = useRef(null)
  const isLocatingRef = useRef(false)

  useEffect(() => {
    if (!map) return

    // Disable default zoom control since we're creating our own
    map.zoomControl?.remove()

    // Create combined control with location + zoom buttons
    const CombinedControl = L.Control.extend({
      options: {
        position: 'bottomright'
      },

      onAdd: function (map) {
        const container = L.DomUtil.create('div', 'leaflet-control leaflet-control-zoom')
        
        // Spot Mode Button (optional, top position)
        let spotButton = null
        if (onSpotClick) {
          spotButton = L.DomUtil.create('a', 'leaflet-control-zoom-in spot-mode-btn', container)
          spotButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"></path>
              <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"></path>
            </svg>
          `
          spotButton.title = 'Spot Mode'
          spotButton.href = '#'
          spotButton.setAttribute('role', 'button')
          spotButton.setAttribute('aria-label', 'Activate Spot Mode')
        }
        
        // My Location Button
        const locationButton = L.DomUtil.create('a', 'leaflet-control-zoom-in', container)
        locationButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"></circle>
            <circle cx="12" cy="12" r="3"></circle>
            <line x1="12" y1="2" x2="12" y2="5"></line>
            <line x1="12" y1="19" x2="12" y2="22"></line>
            <line x1="2" y1="12" x2="5" y2="12"></line>
            <line x1="19" y1="12" x2="22" y2="12"></line>
          </svg>
        `
        locationButton.title = 'Show my location'
        locationButton.href = '#'
        locationButton.setAttribute('role', 'button')
        locationButton.setAttribute('aria-label', 'Show my location')

        // Zoom In Button
        const zoomInButton = L.DomUtil.create('a', 'leaflet-control-zoom-in', container)
        zoomInButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
            <line x1="11" y1="8" x2="11" y2="14"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        `
        zoomInButton.title = 'Zoom in'
        zoomInButton.href = '#'
        zoomInButton.setAttribute('role', 'button')
        zoomInButton.setAttribute('aria-label', 'Zoom in')

        // Zoom Out Button  
        const zoomOutButton = L.DomUtil.create('a', 'leaflet-control-zoom-out', container)
        zoomOutButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        `
        zoomOutButton.title = 'Zoom out'
        zoomOutButton.href = '#'
        zoomOutButton.setAttribute('role', 'button')
        zoomOutButton.setAttribute('aria-label', 'Zoom out')

        // Prevent map interactions when clicking controls
        L.DomEvent.disableClickPropagation(container)
        L.DomEvent.disableScrollPropagation(container)

        // Spot Mode Button Handler (if provided)
        if (spotButton && onSpotClick) {
          L.DomEvent.on(spotButton, 'click', (e) => {
            trackSpotModeActivation('zoom_controls')
            L.DomEvent.preventDefault(e)
            onSpotClick()
          })
        }

        // Location Button Handler
        L.DomEvent.on(locationButton, 'click', (e) => {
          L.DomEvent.preventDefault(e)
          
          if (isLocatingRef.current) return

          // If external handler provided, use it (e.g., from search panel)
          if (onLocationClick) {
            onLocationClick()
            return
          }

          // Otherwise, use built-in geolocation (fallback)
          // Check if geolocation is available
          if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser')
            return
          }

          // Show loading state
          isLocatingRef.current = true
          const originalContent = locationButton.innerHTML
          locationButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
          `
          locationButton.style.color = '#3b82f6'

          // Get current location
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords
              
              // Fly to user location, maintaining current zoom level
              const currentZoom = map.getZoom()
              map.flyTo([latitude, longitude], currentZoom, {
                duration: 1.5 // Smooth animation for better UX
              })

              // Track analytics
              trackMapPan({ lat: latitude, lng: longitude }, 'my_location_button')

              // Reset button
              isLocatingRef.current = false
              locationButton.innerHTML = originalContent
              locationButton.style.color = ''
            },
            (error) => {
              // Log as warning - user action required, not a system error
              logger.warn('Geolocation failed', { code: error.code, message: error.message })
              
              let errorMessage = 'Unable to get your location'
              if (error.code === error.PERMISSION_DENIED) {
                errorMessage = 'Location access denied. Please enable location permissions.'
              } else if (error.code === error.POSITION_UNAVAILABLE) {
                errorMessage = 'Location information unavailable.'
              } else if (error.code === error.TIMEOUT) {
                errorMessage = 'Location request timed out.'
              }
              
              alert(errorMessage)

              // Reset button
              isLocatingRef.current = false
              locationButton.innerHTML = originalContent
              locationButton.style.color = ''
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          )
        })

        // Zoom In Handler
        L.DomEvent.on(zoomInButton, 'click', (e) => {
          L.DomEvent.preventDefault(e)
          const currentZoom = map.getZoom()
          map.zoomIn()
          trackMapZoom(currentZoom + 1, 'zoom_in_button')
        })

        // Zoom Out Handler
        L.DomEvent.on(zoomOutButton, 'click', (e) => {
          L.DomEvent.preventDefault(e)
          const currentZoom = map.getZoom()
          map.zoomOut()
          trackMapZoom(currentZoom - 1, 'zoom_out_button')
        })

        return container
      },

      onRemove: function () {
        // Cleanup if needed
      }
    })

    // Add control to map
    const control = new CombinedControl()
    control.addTo(map)
    controlRef.current = control

    // Add custom styles for animations
    const style = document.createElement('style')
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)

    // Cleanup
    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current)
      }
      document.head.removeChild(style)
    }
  }, [map, trackMapPan, trackMapZoom])

  return null // This component doesn't render anything
}
